import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { TransferServiceImpl } from '../../../domain/Transfer/usecases';
import { BopCode, SwiftCode } from '../../../domain/Transfer/entities';
import { RoutingTransferTypes } from '../../../domain/Transfer/entities/RoutingResponseDTO';
import { Country } from '../../../domain/Country/entities';

const useBopAndVocCodesEndpointsData = (
  service: TransferServiceImpl,
  formCountry: string,
  transferType: RoutingTransferTypes,
  countries: Country[],
  orderCurrency: string,
  selectedBankSwiftCode: Nullable<SwiftCode>,
) => {
  const isExternalUnknownBankTransferType =
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY;

  const isExternalTransfer =
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY;

  const isInternalTransfer =
    transferType === RoutingTransferTypes.INTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.INTERNAL_OTHER_CURRENCY;

  const isIntraBankTransfer =
    transferType === RoutingTransferTypes.INTRABANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY;

  const filteredCountries = countries.filter(c => c.description !== '');

  const bankCountry = selectedBankSwiftCode?.country ? selectedBankSwiftCode.country : formCountry;

  const foundCountryId = filteredCountries.find(c => c.description === bankCountry)?.serialNumber;

  const isRuCreditCurrency = orderCurrency === '25';

  // sending transfer outside of RU with RUB currency
  const sendingTransferOutsideRusWithRubCurrency =
    isDefined(isRuCreditCurrency) &&
    foundCountryId !== 199 &&
    (isExternalUnknownBankTransferType || isExternalTransfer || isInternalTransfer || isIntraBankTransfer);

  const shouldFetchVoCodes = isRuCreditCurrency && (isExternalTransfer || isExternalUnknownBankTransferType);

  const {
    data: vocCodesData,
    error: vocCodesErrorData,
    isLoading: isLoadingVocCodes,
  } = useInvokeSWRService<BopCode[]>(service.GetVocCodes, null, shouldFetchVoCodes, 'transferService.GetVopCodes');

  // looking for India or Jordan or UEA check to get BopCodes
  const isUKCountry = foundCountryId === 74; // United Kingdom
  const isGBPCurrency = orderCurrency === '4'; // GBP currency
  const isCountryThatNeedsBopCode = foundCountryId === 76 || foundCountryId === 78 || foundCountryId === 68 || (isUKCountry && isGBPCurrency);
  const isSendingRubAndNeedsBopCode = isCountryThatNeedsBopCode && sendingTransferOutsideRusWithRubCurrency;
  const shouldGetBopCodes =
    (isCountryThatNeedsBopCode && (isExternalUnknownBankTransferType || isExternalTransfer)) ||
    (isSendingRubAndNeedsBopCode && (isExternalUnknownBankTransferType || isExternalTransfer));

  const {
    data: bopCodesData,
    error: bopCodesErrorData,
    isLoading: isLoadingBopCodes,
  } = useInvokeSWRService<BopCode[]>(
    service.GetBopCodes,
    {
      selectedCountryId: foundCountryId,
    },
    shouldGetBopCodes,
    'transferService.GetBopCodes',
  );

  const error = bopCodesErrorData || vocCodesErrorData;

  const shouldShowErrorContent = (isExternalUnknownBankTransferType || isExternalTransfer) && error && !isEmpty(error);

  return {
    bopCodesData,
    isLoadingBopCodes,
    vocCodesData,
    isLoadingVocCodes,
    error,
    shouldShowErrorContent,
  };
};

export { useBopAndVocCodesEndpointsData };
